import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';

import classNames from 'classnames';

import { is } from 'immutable';
import { connect } from 'react-redux';


import { throttle } from 'lodash';

import { reportSensitive } from 'mastodon/actions/interactions';
import { Blurhash } from 'mastodon/components/blurhash';
import { Icon } from 'mastodon/components/icon';
import { CloseIcon } from 'mastodon/components/icons/close';
import { EyeSlashIcon } from 'mastodon/components/icons/eye_slash';
import { FullScreenIcon } from 'mastodon/components/icons/fullscreen';
import { FullScreenExitIcon } from 'mastodon/components/icons/fullscreen_exit';
import { PauseIcon } from 'mastodon/components/icons/pause';
import { PlayIcon } from 'mastodon/components/icons/play';
import { VolumeOffIcon } from 'mastodon/components/icons/volume_off';
import { VolumeUpIcon } from 'mastodon/components/icons/volume_up';
import { playerSettings } from 'mastodon/settings';

import { displayMedia, useBlurhash } from '../../initial_state';
import { isFullscreen, requestFullscreen, exitFullscreen } from '../ui/util/fullscreen';

const messages = defineMessages({
  play: { id: 'video.play', defaultMessage: 'Play' },
  pause: { id: 'video.pause', defaultMessage: 'Pause' },
  mute: { id: 'video.mute', defaultMessage: 'Mute sound' },
  unmute: { id: 'video.unmute', defaultMessage: 'Unmute sound' },
  hide: { id: 'video.hide', defaultMessage: 'Hide video' },
  expand: { id: 'video.expand', defaultMessage: 'Expand video' },
  close: { id: 'video.close', defaultMessage: 'Close video' },
  fullscreen: { id: 'video.fullscreen', defaultMessage: 'Full screen' },
  exit_fullscreen: { id: 'video.exit_fullscreen', defaultMessage: 'Exit full screen' },
});

export const formatTime = secondsNum => {
  let hours   = Math.floor(secondsNum / 3600);
  let minutes = Math.floor((secondsNum - (hours * 3600)) / 60);
  let seconds = secondsNum - (hours * 3600) - (minutes * 60);

  if (hours   < 10) hours   = '0' + hours;
  if (minutes < 10) minutes = '0' + minutes;
  if (seconds < 10) seconds = '0' + seconds;

  return (hours === '00' ? '' : `${hours}:`) + `${minutes}:${seconds}`;
};

export const findElementPosition = el => {
  let box;

  if (el.getBoundingClientRect && el.parentNode) {
    box = el.getBoundingClientRect();
  }

  if (!box) {
    return {
      left: 0,
      top: 0,
    };
  }

  const docEl = document.documentElement;
  const body  = document.body;

  const clientLeft = docEl.clientLeft || body.clientLeft || 0;
  const scrollLeft = window.pageXOffset || body.scrollLeft;
  const left       = (box.left + scrollLeft) - clientLeft;

  const clientTop = docEl.clientTop || body.clientTop || 0;
  const scrollTop = window.pageYOffset || body.scrollTop;
  const top       = (box.top + scrollTop) - clientTop;

  return {
    left: Math.round(left),
    top: Math.round(top),
  };
};

export const getPointerPosition = (el, event) => {
  const position = {};
  const box = findElementPosition(el);
  const boxW = el.offsetWidth;
  const boxH = el.offsetHeight;
  const boxY = box.top;
  const boxX = box.left;

  let pageY = event.pageY;
  let pageX = event.pageX;

  if (event.changedTouches) {
    pageX = event.changedTouches[0].pageX;
    pageY = event.changedTouches[0].pageY;
  }

  position.y = Math.max(0, Math.min(1, (pageY - boxY) / boxH));
  position.x = Math.max(0, Math.min(1, (pageX - boxX) / boxW));

  return position;
};

export const fileNameFromURL = str => {
  const url      = new URL(str);
  const pathname = url.pathname;
  const index    = pathname.lastIndexOf('/');

  return pathname.slice(index + 1);
};

class Video extends PureComponent {
  static propTypes = {
    preview: PropTypes.string,
    frameRate: PropTypes.string,
    aspectRatio: PropTypes.string,
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    lang: PropTypes.string,
    sensitive: PropTypes.bool,
    currentTime: PropTypes.number,
    onOpenVideo: PropTypes.func,
    onCloseVideo: PropTypes.func,
    detailed: PropTypes.bool,
    editable: PropTypes.bool,
    alwaysVisible: PropTypes.bool,
    visible: PropTypes.bool,
    onToggleVisibility: PropTypes.func,
    deployPictureInPicture: PropTypes.func,
    intl: PropTypes.object.isRequired,
    blurhash: PropTypes.string,
    autoPlay: PropTypes.bool,
    volume: PropTypes.number,
    muted: PropTypes.bool,
    componentIndex: PropTypes.number,
    autoFocus: PropTypes.bool,
    statusId: PropTypes.string.isRequired,
    accountId: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  static defaultProps = {
    frameRate: '25',
  };

  state = {
    currentTime: 0,
    duration: 0,
    volume: 0.5,
    paused: true,
    dragging: false,
    fullscreen: false,
    hovered: false,
    muted: true,
    revealed: this.props.visible !== undefined ? this.props.visible : (displayMedia !== 'hide_all' && !this.props.sensitive || displayMedia === 'show_all'),
    reported: false,
    controlsVisible: true,
    controlsTimeout: null,
    isParentVideoModal: false,
    loading: true,
    isVideoModal: false,
  };

  setPlayerRef = c => {
    this.player = c;

    if (c) {
      const isParentVideoModal = c.parentElement?.parentElement?.classList.contains('video-modal') ||
                                 c.parentElement?.parentElement?.classList.contains('video-modal__container');

      const isVideoModal = isParentVideoModal;

      this.setState({ isParentVideoModal, isVideoModal }, () => {
        if (isParentVideoModal && isVideoModal && this.state.paused) {
          c.parentElement?.parentElement?.classList.add('paused');
          const navVideoModal = document.querySelector('.media-modal__navigation');
          if (navVideoModal) {
            navVideoModal.style.setProperty('display', 'block', 'important');
          }
        }
      });

      this._setupIntersectionObserver();
    }
  };

  setVideoRef = c => {
    this.video = c;

    if (this.video) {
      this.setState({ volume: this.video.volume, muted: this.video.muted });
    }
  };

  setSeekRef = c => {
    this.seek = c;
  };

  setVolumeRef = c => {
    this.volume = c;
  };

  handleClickRoot = e => e.stopPropagation();

  showControls = () => {
    this.setState({ controlsVisible: true });
    this.resetControlsTimeout();
  };

  hideControls = () => {
    if (!this.state.paused && !this.state.dragging) {
      this.setState({ controlsVisible: false });
    }
  };

  resetControlsTimeout = () => {
    if (this.state.controlsTimeout) {
      clearTimeout(this.state.controlsTimeout);
    }

    if (this.state.isParentVideoModal || this.state.paused) {
      this.setState({ controlsVisible: true });
      return;
    }

    const timeout = setTimeout(() => {
      this.hideControls();
    }, 3000);

    this.setState({ controlsTimeout: timeout });
  };

  handlePlay = () => {
    this.setState({ paused: false, loading: false });
    this.resetControlsTimeout();
    this._updateTime();
  };

  handlePause = () => {
    this.setState({ paused: true, controlsVisible: true });
    if (this.state.controlsTimeout) {
      clearTimeout(this.state.controlsTimeout);
    }
  };

  _updateTime() {
    requestAnimationFrame(() => {
      if (!this.video) return;

      this.handleTimeUpdate();

      if (!this.state.paused) {
        this._updateTime();
      }
    });
  }

  handleTimeUpdate = () => {
    this.setState({
      currentTime: this.video.currentTime,
      duration: this.video.duration,
    });
  };

  handleVolumeMouseDown = e => {
    document.addEventListener('mousemove', this.handleMouseVolSlide, true);
    document.addEventListener('mouseup', this.handleVolumeMouseUp, true);
    document.addEventListener('touchmove', this.handleMouseVolSlide, true);
    document.addEventListener('touchend', this.handleVolumeMouseUp, true);

    this.handleMouseVolSlide(e);

    e.preventDefault();
    e.stopPropagation();
  };

  handleVolumeMouseUp = () => {
    document.removeEventListener('mousemove', this.handleMouseVolSlide, true);
    document.removeEventListener('mouseup', this.handleVolumeMouseUp, true);
    document.removeEventListener('touchmove', this.handleMouseVolSlide, true);
    document.removeEventListener('touchend', this.handleVolumeMouseUp, true);
  };

  handleMouseVolSlide = throttle(e => {
    const { x } = getPointerPosition(this.volume, e);

    if (!isNaN(x)) {
      this.setState((state) => ({ volume: x, muted: state.muted && x === 0 }), () => {
        this._syncVideoToVolumeState(x);
        this._saveVolumeState(x);
      });
    }
  }, 15);

  handleMouseDown = e => {
    document.addEventListener('mousemove', this.handleMouseMove, true);
    document.addEventListener('mouseup', this.handleMouseUp, true);
    document.addEventListener('touchmove', this.handleMouseMove, true);
    document.addEventListener('touchend', this.handleMouseUp, true);

    this.setState({ dragging: true });
    this.video.pause();
    this.handleMouseMove(e);

    e.preventDefault();
    e.stopPropagation();
  };

  handleMouseUp = () => {
    document.removeEventListener('mousemove', this.handleMouseMove, true);
    document.removeEventListener('mouseup', this.handleMouseUp, true);
    document.removeEventListener('touchmove', this.handleMouseMove, true);
    document.removeEventListener('touchend', this.handleMouseUp, true);

    this.setState({ dragging: false }, () => {
      if (!this.state.paused) {
        const playPromise = this.video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            if (error.name !== 'AbortError') {
              console.error('Error playing video:', error);
            }
          });
        }
      }
    });
  };

  handleMouseMove = throttle(e => {
    const { x } = getPointerPosition(this.seek, e);
    const currentTime = this.video.duration * x;

    if (!isNaN(currentTime)) {
      this.setState({ currentTime }, () => {
        this.video.currentTime = currentTime;
      });
    }
  }, 15);

  seekBy(time) {
    const currentTime = this.video.currentTime + time;

    if (!isNaN(currentTime)) {
      this.setState({ currentTime }, () => {
        this.video.currentTime = currentTime;
      });
    }
  }

  handleVideoKeyDown = e => {
    if (e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      this.togglePlay();
    }
  };

  handleKeyDown = e => {
    const frameTime = 1 / this.getFrameRate();

    switch (e.key) {
    case 'k':
      e.preventDefault();
      e.stopPropagation();
      this.togglePlay();
      break;
    case 'm':
      e.preventDefault();
      e.stopPropagation();
      this.toggleMute();
      break;
    case 'f':
      e.preventDefault();
      e.stopPropagation();
      this.toggleFullscreen();
      break;
    case 'j':
      e.preventDefault();
      e.stopPropagation();
      this.seekBy(-10);
      break;
    case 'l':
      e.preventDefault();
      e.stopPropagation();
      this.seekBy(10);
      break;
    case ',':
      e.preventDefault();
      e.stopPropagation();
      this.seekBy(-frameTime);
      break;
    case '.':
      e.preventDefault();
      e.stopPropagation();
      this.seekBy(frameTime);
      break;
    }

    if (this.state.fullscreen) {
      e.preventDefault();
      e.stopPropagation();

      if (e.key === 'Escape') {
        exitFullscreen();
      }
    }
  };

  handleReport = () => {
    const { dispatch, statusId, accountId } = this.props;
    dispatch(reportSensitive(statusId, accountId, () => {
      this.setState({ revealed: false, reported: true });
    }));
  };

  togglePlay = () => {
    if (this.state.paused) {
      this.setState({ paused: false }, () => {
        const playPromise = this.video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            if (error.name === 'NotAllowedError') {
              this.setState({ paused: true });
            } else if (error.name !== 'AbortError') {
              console.error('Error playing video:', error);
            }
          });
        }
      });
    } else {
      this.setState({ paused: true }, () => {
        this.video.pause();
        this.showControls();
      });
    }
  };

  toggleFullscreen = () => {
    if (this.isIOSWebKit()) {
      if (!this.state.isVideoModal && this.props.onOpenVideo) {
        this.handleOpenVideo();
      }

      return;
    }

    if (isFullscreen()) {
      exitFullscreen();
    } else {
      requestFullscreen(this.player);
    }
  };

  isIOSWebKit = () => {
    return /iP(hone|od|ad)/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  isSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  componentDidMount() {
    document.addEventListener('fullscreenchange', this.handleFullscreenChange, true);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange, true);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange, true);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange, true);

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('blur', this.handleWindowBlur);
    window.addEventListener('pagehide', this.handlePageHide);

    if (this.isSafari()) {
      document.addEventListener('touchstart', this.handleUserInteraction, { once: true });
      document.addEventListener('click', this.handleUserInteraction, { once: true });
    }

    this._syncVideoFromLocalStorage();
    this._setupIntersectionObserver();
  }

  _setupIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (!this.player) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            this.deactivateVideo();
          }
          // When coming back into view: stay paused, user must press play again
        });
      },
      { threshold: 0.2 }, // pause once < 20 % of the video is visible
    );

    this.intersectionObserver.observe(this.player);
  }

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.deactivateVideo();
    }
  };

  handleWindowBlur = () => {
    this.deactivateVideo();
  };

  handlePageHide = () => {
    this.deactivateVideo();
  };

  deactivateVideo = () => {
    if (!this.video) return;

    if (!this.video.paused) this.video.pause();
    if (!this.state.paused) this.setState({ paused: true });

    const fullscreenElement = isFullscreen();
    if (fullscreenElement && (fullscreenElement === this.player || fullscreenElement.contains(this.video))) {
      exitFullscreen();
    } else if (this.video.webkitDisplayingFullscreen && this.video.webkitExitFullscreen) {
      this.video.webkitExitFullscreen();
    }
  };

  handleUserInteraction = () => {
    this.userHasInteracted = true;

    if (this.state.isVideoModal && this.state.paused && this.video) {
      this.forcePlayInModal();
    } else if (this.props.autoPlay && this.state.paused && this.video) {
      const playPromise = this.video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          if (error.name !== 'AbortError') {
            console.error('Still cannot autoplay after user interaction');
          }
        });
      }
    }
  };

  handleVideoClick = (e) => {
    if (this.state.isVideoModal) {
      e.stopPropagation();
      this.togglePlay();
    } else {
      this.handleOpenVideo();
    }
  };

  componentWillUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    document.removeEventListener('fullscreenchange', this.handleFullscreenChange, true);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange, true);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange, true);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange, true);

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('blur', this.handleWindowBlur);
    window.removeEventListener('pagehide', this.handlePageHide);

    if (this.isSafari()) {
      document.removeEventListener('touchstart', this.handleUserInteraction);
      document.removeEventListener('click', this.handleUserInteraction);
    }

    if (this.state.controlsTimeout) {
      clearTimeout(this.state.controlsTimeout);
    }

    /*
    if (!this.state.paused && this.video && this.props.deployPictureInPicture) {
      this.props.deployPictureInPicture('video', {
        src: this.props.src,
        currentTime: this.video.currentTime,
        muted: this.video.muted,
        volume: this.video.volume,
      });
    }
    */
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!is(nextProps.visible, this.props.visible) && nextProps.visible !== undefined) {
      this.setState({ revealed: nextProps.visible });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.revealed && !this.state.revealed && this.video && !this.video.paused) {
      this.video.pause();
    }

    if (this.state.isParentVideoModal && this.state.isVideoModal && prevState.paused !== this.state.paused) {
      const parentContainer = this.player?.parentElement?.parentElement;
      const navVideoModal = document.querySelector('.media-modal__navigation');

      if (parentContainer?.classList.contains('video-modal__container')) {
        parentContainer.classList.toggle('paused', this.state.paused);
      }

      if (navVideoModal && window.innerWidth <= 768) {
        navVideoModal.style.setProperty('display', this.state.paused ? 'block' : 'none', 'important');
      }
    }

    if (!prevState.isVideoModal && this.state.isVideoModal && this.state.paused && this.video) {
      setTimeout(() => {
        this.forcePlayInModal();
      }, 300);
    }
  }



  handleFullscreenChange = () => {
    this.setState({ fullscreen: isFullscreen() });
  };

  handleMouseEnter = () => {
    this.setState({ hovered: true });
    this.showControls();
  };

  handleMouseLeave = () => {
    this.setState({ hovered: false });
    this.resetControlsTimeout();
  };

  toggleMute = () => {
    const muted = !(this.video.muted || this.state.volume === 0);

    this.setState((state) => ({ muted, volume: Math.max(state.volume || 0.5, 0.05) }), () => {
      this._syncVideoToVolumeState();
      this._saveVolumeState();
    });
  };

  _syncVideoToVolumeState = (volume = null, muted = null) => {
    if (!this.video) {
      return;
    }

    this.video.volume = volume ?? this.state.volume;
    this.video.muted = muted ?? this.state.muted;
  };

  _saveVolumeState = (volume = null, muted = null) => {
    playerSettings.set('volume', volume ?? this.state.volume);
    playerSettings.set('muted', muted ?? this.state.muted);
  };

  _syncVideoFromLocalStorage = () => {
    this.setState({ volume: playerSettings.get('volume') ?? 0.5, muted: playerSettings.get('muted') ?? true }, () => {
      this._syncVideoToVolumeState();
    });
  };

  toggleReveal = () => {
    if (this.props.onToggleVisibility) {
      this.props.onToggleVisibility();
    } else {
      this.setState({ revealed: !this.state.revealed });
    }
  };

  handleLoadedData = () => {
    const { currentTime, volume, autoPlay } = this.props;

    if (currentTime) {
      this.video.currentTime = currentTime;
    }

    if (volume !== undefined) {
      this.video.volume = volume;
    }

    this.video.muted = true;
    this.video.playsInline = true;
    this.video.setAttribute('webkit-playsinline', 'true');
    this.video.setAttribute('playsinline', 'true');

    if (autoPlay && this.state.paused) {
      this.setState({ loading: true });

      if (this.isSafari() && this.state.isVideoModal) {
        this.setState({ loading: false, paused: true });
        return;
      }

      setTimeout(() => {
        const playPromise = this.video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              this.setState({ paused: false, loading: false });
            })
            .catch(error => {
              console.error('Autoplay error:', error.name);
              this.setState({ paused: true, loading: false });
            });
        }
      }, 100);
    } else {
      this.setState({ loading: false });
    }
  };

  forcePlayInModal = () => {
    if (this.video && this.state.paused) {
      const playPromise = this.video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.setState({ paused: false });
          })
          .catch(error => {
            console.error('Cannot play video:', error);
          });
      }
    }
  };

  handleProgress = () => {
    const lastTimeRange = this.video.buffered.length - 1;

    if (lastTimeRange > -1) {
      this.setState({ buffer: Math.ceil(this.video.buffered.end(lastTimeRange) / this.video.duration * 100) });
    }
  };

  handleVolumeChange = () => {
    this.setState({ volume: this.video.volume, muted: this.video.muted });
    this._saveVolumeState(this.video.volume, this.video.muted);
  };

  handleOpenVideo = () => {
    this.togglePlay();
    this.showControls();

    if (this.props.onOpenVideo) {
      this.props.onOpenVideo(this.props.lang, {
        startTime: this.video.currentTime,
        autoPlay: true,
        muted: true,
        loop: true,
        defaultVolume: this.state.volume,
        componentIndex: this.props.componentIndex,
      });
    }
  };

  handleCloseVideo = () => {
    this.deactivateVideo();
    this.props.onCloseVideo?.();
  };

  handleExitOverlay = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (this.state.fullscreen) {
      this.deactivateVideo();
    } else if (this.state.isVideoModal && this.props.onCloseVideo) {
      this.handleCloseVideo();
    } else {
      this.deactivateVideo();
    }
  };

  getFrameRate() {
    if (this.props.frameRate && isNaN(this.props.frameRate)) {
      return this.props.frameRate.split('/').reduce((p, c) => p / c);
    }

    return this.props.frameRate;
  }

  handleCanPlay = () => {
    if (this.props.autoPlay && this.state.paused && !this.state.loading) {
      const playPromise = this.video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          if (error.name === 'NotAllowedError') {
            console.error('Safari requires user interaction for autoplay');
          }
        });
      }
    }
  };

  render() {
    const { preview, src, aspectRatio, onCloseVideo, onOpenVideo, intl, alt, lang, detailed, sensitive, editable, blurhash, autoFocus } = this.props;
    const { currentTime, duration, volume, buffer, dragging, paused, fullscreen, hovered, revealed, reported, controlsVisible, isParentVideoModal, isVideoModal, loading } = this.state;
    const progress = Math.min((currentTime / duration) * 100, 100);
    const muted = this.state.muted || volume === 0;
    const showFullscreenButton = !this.isIOSWebKit() || (!isVideoModal && onOpenVideo);

    let preload;

    if (this.props.currentTime || fullscreen || dragging) {
      preload = 'auto';
    } else if (detailed) {
      preload = 'metadata';
    } else {
      preload = 'none';
    }

    const spoilerButton = (
      <div className='spoiler-button'>
        <button type='button' className='spoiler-button__overlay' onClick={!reported && this.toggleReveal} disabled={reported}>
          <span className='spoiler-button__overlay__label'>
            <EyeSlashIcon
              className='spoiler-button__icon'
              style={{ width: '36px', height: '36px', marginRight: '8px'}}
            />
            {sensitive ? (
              <FormattedMessage id='status.sensitive_warning' defaultMessage='Sensitive content' />
            ) : (
              <FormattedMessage id='status.media_hidden' defaultMessage='Media hidden' />
            )}
            <span className='spoiler-button__overlay__action'>
              {reported ? (
                <FormattedMessage id='status.reported_hidden' defaultMessage='Post hidden due to reported violation' />
              ) : (
                <FormattedMessage id='status.media.show' defaultMessage='Click to show' />
              )}
            </span>
          </span>
        </button>
      </div>
    );

    const reportButton = (sensitive && revealed && !reported) && (
      <div className='media-gallery__actions'>
        <button className='media-gallery__actions__pill' onClick={this.handleReport}>
          <FormattedMessage id='media_gallery.report' defaultMessage='Report' />
        </button>
      </div>
    );

    return (
      <div
        style={{
          margin: window.innerWidth <= 630 ? '16px 0' : 0,
          maxHeight: '70svh',
          width: '100%',
          overflow: 'hidden'
        }}
        className='video-player-wrapper'
      >
        <div
          role='menuitem'
          className={classNames("video-player", {
            inactive: !revealed,
            detailed,
            fullscreen,
            editable,
            paused: paused,
            "controls-hidden": !controlsVisible,
          })}
          style={{
            aspectRatio,
            maxHeight: window.innerWidth <= 630 ? '50vh' : '70vh',
            minHeight: window.innerWidth <= 630 ? 'unset' : '50vh',
            margin: 0,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          ref={this.setPlayerRef}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          onMouseMove={this.showControls}
          onClick={this.handleClickRoot}
          onKeyDown={this.handleKeyDown}
          tabIndex={0}
        >
          <Blurhash
            hash={blurhash}
            className={classNames('media-gallery__preview', {
              'media-gallery__preview--hidden': revealed,
            })}
            dummy={!useBlurhash}
          />

          {(revealed || editable) && (
            <div className={isVideoModal ? 'video-player__container' : 'video-wrapper'}>
              {isParentVideoModal && loading && (
                <div className='video-player__loading'>
                  <div className='video-player__spinner' />
                </div>
              )}
              <video
                ref={this.setVideoRef}
                src={src}
                poster={preview}
                preload={preload}
                role='button'
                tabIndex={0}
                aria-label={alt}
                title={alt}
                lang={lang}
                onClick={this.handleVideoClick}
                onKeyDown={this.handleVideoKeyDown}
                onPlay={this.handlePlay}
                onPause={this.handlePause}
                onLoadedData={this.handleLoadedData}
                onProgress={this.handleProgress}
                onCanPlay={this.handleCanPlay}
                onVolumeChange={this.handleVolumeChange}
                style={{ width: '100%' }}
                autoPlay={!this.isSafari() || !isVideoModal}
                playsInline
                loop
                controlsList='nodownload'
                muted
              />
            </div>
          )}

          {!revealed && spoilerButton}
          {reportButton}

          {fullscreen && (
            <button
              type='button'
              title={intl.formatMessage(messages.exit_fullscreen)}
              aria-label={intl.formatMessage(messages.exit_fullscreen)}
              className='video-player__close-overlay'
              onClick={this.handleExitOverlay}
            >
              <Icon id='times' icon={CloseIcon} />
            </button>
          )}

          <div className={classNames('video-player__controls', { active: isParentVideoModal || paused || hovered || controlsVisible })}>
            <div className='video-player__seek' onMouseDown={this.handleMouseDown} ref={this.setSeekRef}>
              <div className='video-player__seek__buffer' style={{ width: `${buffer}%` }} />
              <div className='video-player__seek__progress' style={{ width: `${progress}%` }} />

              <span
                className={classNames('video-player__seek__handle', { active: dragging })}
                tabIndex={0}
                style={{ left: `${progress}%` }}
                onKeyDown={this.handleVideoKeyDown}
              />
            </div>

            <div className='video-player__buttons-bar'>
              <div className='video-player__buttons left'>
                <button type='button' title={intl.formatMessage(paused ? messages.play : messages.pause)} aria-label={intl.formatMessage(paused ? messages.play : messages.pause)} className='player-button pause' onClick={this.togglePlay} autoFocus={autoFocus}><Icon id={paused ? 'play' : 'pause'} icon={paused ? PlayIcon : PauseIcon} /></button>
                <button type='button' title={intl.formatMessage(muted ? messages.unmute : messages.mute)} aria-label={intl.formatMessage(muted ? messages.unmute : messages.mute)} className='player-button' onClick={this.toggleMute}><Icon id={muted ? 'volume-off' : 'volume-up'} icon={muted ? VolumeOffIcon : VolumeUpIcon} /></button>

                <div className={classNames('video-player__volume', { active: this.state.hovered })} onMouseDown={this.handleVolumeMouseDown} ref={this.setVolumeRef}>
                  <div className='video-player__volume__current' style={{ width: `${muted ? 0 : volume * 100}%` }} />

                  <span
                    className={classNames('video-player__volume__handle')}
                    tabIndex={0}
                    style={{ left: `${muted ? 0 : volume * 100}%` }}
                  />
                </div>

                {(detailed || fullscreen) && (
                  <span className='video-player__time'>
                    <span className='video-player__time-current'>{formatTime(Math.floor(currentTime))}</span>
                    <span className='video-player__time-sep'>/</span>
                    <span className='video-player__time-total'>{formatTime(Math.floor(duration))}</span>
                  </span>
                )}
              </div>

              <div className='video-player__buttons right'>
                {onCloseVideo && <button type='button' title={intl.formatMessage(messages.close)} aria-label={intl.formatMessage(messages.close)} className='player-button' onClick={this.handleCloseVideo}><Icon id='compress' icon={FullScreenExitIcon} /></button>}
                {showFullscreenButton && <button type='button' title={intl.formatMessage(fullscreen ? messages.exit_fullscreen : messages.fullscreen)} aria-label={intl.formatMessage(fullscreen ? messages.exit_fullscreen : messages.fullscreen)} className='player-button' onClick={this.toggleFullscreen}><Icon id={fullscreen ? 'compress' : 'arrows-alt'} icon={fullscreen ? FullScreenExitIcon : FullScreenIcon} /></button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect()(injectIntl(Video));
